/* eslint-disable @typescript-eslint/no-non-null-assertion,@typescript-eslint/consistent-type-assertions */
/* eslint-disable no-eq-null */
import { css } from "@emotion/css";
import { RadioButton, RadioButtonGroup, Tooltip } from "@octopusdeploy/design-system-components";
import { themeTokens } from "@octopusdeploy/design-system-tokens";
import { ActionExecutionLocation } from "@octopusdeploy/octopus-server-client";
import { noOp } from "@octopusdeploy/utilities";
import * as React from "react";
import type { RunOn } from "~/areas/projects/components/Process/types";
import { TargetRoles, ExecutionLocation } from "~/areas/projects/components/Process/types";
import { ExecuteOctopusServer } from "~/components/Images/ExecutionLocation/ExecuteOctopusServer";
import { ExecuteOctopusServerRoles } from "~/components/Images/ExecutionLocation/ExecuteOctopusServerRoles";
import { ExecuteTargets } from "~/components/Images/ExecutionLocation/ExecuteTargets";
import { ExecuteWorker } from "~/components/Images/ExecutionLocation/ExecuteWorker";
import { ExecuteWorkerRoles } from "~/components/Images/ExecutionLocation/ExecuteWorkerRoles";
import TransitionAnimation from "~/components/TransitionAnimation/TransitionAnimation";
import DelegatedFormSection from "~/components/form/Sections/DelegatedFormSection";
import { Note } from "~/components/form/index";
import styles from "./style.module.less";
interface PlatformHubExecutionPlanProps {
    expandedByDefault: boolean;
    executionLocation: ActionExecutionLocation;
    runOn: RunOn;
    targetRoleOption: TargetRoles;
    canRunOnWorker: boolean;
}
enum WorkerPoolSelect {
    Pool = "Pool",
    Variable = "Variable"
}
type Props = PlatformHubExecutionPlanProps;
class PlatformHubExecutionPlan extends React.Component<Props> {
    constructor(props: Props) {
        super(props);
    }
    render() {
        return (<div>
                <DelegatedFormSection isExpandedByDefault={this.props.expandedByDefault} errorKey="ActionExecutionLocation" title={<span className={disabledStyles.disabledText}>Execution Location</span>} help="Where will Octopus run this step?" accessibleName={"Execution Location"}>
                    <Tooltip content={"This field requires values specific to the project."} position={"bottom"} display={"block"}>
                        {this.renderRunOnOptions()}
                    </Tooltip>
                </DelegatedFormSection>
            </div>);
    }
    private renderRunOnOptions = () => {
        const imageHeight = "5rem";
        const alwaysOnServer = this.props.executionLocation === ActionExecutionLocation.AlwaysOnServer;
        if (alwaysOnServer && this.props.targetRoleOption !== TargetRoles.Optional) {
            //This is different to the standard execution plan test as we dont have worker pools in the platform hub
            if (this.props.canRunOnWorker) {
                // Workers
                return (<div>
                        {this.props.targetRoleOption !== TargetRoles.None ? (<div className={styles.col}>
                                {" "}
                                <span>
                                    This step will run once on a <strong>worker</strong> on behalf of each deployment target
                                </span>
                                <ExecuteWorkerRoles height={imageHeight}/>
                                <Note className={disabledStyles.disabledText}>Execute on a worker on behalf of all the deployment targets with selected target tags.</Note>
                            </div>) : (<div className={styles.col}>
                                {" "}
                                <span>
                                    This step will run once on a <strong>worker</strong>
                                </span>
                                <ExecuteWorker height={imageHeight}/>
                                <Note className={disabledStyles.disabledText}>Execute once on a worker.</Note>
                            </div>)}
                    </div>);
            }
            else {
                // Octopus Server
                return (<div>
                        {this.props.targetRoleOption !== TargetRoles.None ? (<div className={styles.col}>
                                {" "}
                                <span>
                                    This step will run on the <strong>Octopus Server</strong> on behalf of each deployment target
                                </span>
                                <ExecuteOctopusServerRoles height={imageHeight}/>
                                <Note className={disabledStyles.disabledText}>Execute on the Octopus Server on behalf of all the deployment targets with selected target tags.</Note>
                            </div>) : (<div className={styles.col}>
                                {" "}
                                <span>
                                    This step will run on the <strong>Octopus Server</strong>
                                </span>
                                <ExecuteOctopusServer height={imageHeight}/>
                                <Note className={disabledStyles.disabledText}>Execute once on the Octopus Server.</Note>
                            </div>)}
                    </div>);
            }
        }
        if (this.props.executionLocation === ActionExecutionLocation.AlwaysOnTarget) {
            return (<div className={styles.col}>
                    <span>
                        This step will run on each <strong>deployment target</strong>
                    </span>
                    <ExecuteTargets height={imageHeight}/>
                    <Note className={disabledStyles.disabledText}>Execute on each deployment target with the selected target tags.</Note>
                </div>);
        }
        return (<div className={styles.row}>
                {this.renderExecutionLocationRadioGroup(alwaysOnServer)}

                <div className={styles.images}>
                    {this.props.runOn.executionLocation === ExecutionLocation.DeploymentTarget ? (<TransitionAnimation>
                            <ExecuteTargets height={imageHeight}/>
                            <Note className={disabledStyles.disabledText}>Execute on each deployment target with the selected target tags.</Note>
                        </TransitionAnimation>) : (<>
                            {this.props.runOn.executionLocation === ExecutionLocation.ProjectSelectedWorker && (<TransitionAnimation>
                                    <ExecuteWorker height={imageHeight}/>
                                    <Note className={disabledStyles.disabledText}>Execute once on the worker selected in the project.</Note>
                                </TransitionAnimation>)}
                            {this.props.runOn.executionLocation === ExecutionLocation.ProjectSelectedWorkerForRoles && (<TransitionAnimation>
                                    <ExecuteWorkerRoles height={imageHeight}/>
                                    <Note className={disabledStyles.disabledText}>Execute on the worker selected in the project on behalf of all the deployment targets with selected target tags.</Note>
                                </TransitionAnimation>)}
                            {this.props.runOn.executionLocation === ExecutionLocation.OctopusServer && (<TransitionAnimation>
                                    <ExecuteOctopusServer height={imageHeight}/>
                                    <Note className={disabledStyles.disabledText}>Execute once on the Octopus Server.</Note>
                                </TransitionAnimation>)}
                            {this.props.runOn.executionLocation === ExecutionLocation.OctopusServerForRoles && (<TransitionAnimation>
                                    <ExecuteOctopusServerRoles height={imageHeight}/>
                                    <Note className={disabledStyles.disabledText}>Execute on the Octopus Server on behalf of all the deployment targets with selected target tags.</Note>
                                </TransitionAnimation>)}
                            {this.props.runOn.executionLocation === ExecutionLocation.WorkerPool && (<TransitionAnimation>
                                    <ExecuteWorker height={imageHeight}/>
                                    <Note className={disabledStyles.disabledText}>Execute once on a worker.</Note>
                                </TransitionAnimation>)}
                            {this.props.runOn.executionLocation === ExecutionLocation.WorkerPoolForRoles && (<TransitionAnimation>
                                    <ExecuteWorkerRoles height={imageHeight}/>
                                    <Note className={disabledStyles.disabledText}>Execute on a worker on behalf of all the deployment targets with selected target tags.</Note>
                                </TransitionAnimation>)}
                        </>)}
                </div>
            </div>);
    };
    private renderExecutionLocationRadioGroup(alwaysOnServer: boolean) {
        return (<RadioButtonGroup accessibleName={"The location where this step will run"} value={this.props.runOn.executionLocation} onChange={noOp}>
                <RadioButton value={ExecutionLocation.ProjectSelectedWorker} label="Run on a worker selected in the project" disabled={true}/>
                <RadioButton value={ExecutionLocation.ProjectSelectedWorkerForRoles} label="Run on a worker selected in the project on behalf of each deployment target" disabled={true}/>
                {!alwaysOnServer && <RadioButton value={ExecutionLocation.DeploymentTarget} label="Run on each deployment target" disabled={true}/>}
            </RadioButtonGroup>);
    }
    static displayName = "PlatformHubExecutionPlan";
}
const disabledStyles = {
    disabledText: css({
        color: `${themeTokens.color.text.disabled} !important`,
    }),
};
export default PlatformHubExecutionPlan;
